import "./add-to-cart/add-to-cart";
import "./accordion/accordion";
import "./conditional-details/conditional-details";
import "./video/video";
import "./tab/tab";
import "./price-snippet/price-snippet";
import "./trap-focus/trap-focus";
import "./localization-form/localization-form";
import "./aria-listbox/aria-listbox";
import "./aria-listbox-v2/aria-listbox-v2";
import "./aria-listbox-v2/aria-combobox";
import "./aria-listbox-v2/aria-combobox-trigger";
import "./custom-jumplink/custom-jumplink";
import "./cart/cart";
import "./quantity-input/quantity-input";
import "./info-tooltip/info-tooltip";
import "./markup-modal/markup-modal";
import "./product-in-cart/product-in-cart";
import Swiper from "swiper";
import {
  Autoplay,
  EffectFade,
  Navigation,
  Pagination,
  Mousewheel,
  Thumbs,
  EffectCreative,
} from "swiper/modules";
import { register } from "swiper/element/bundle";
import "./cart/saveCompanyId";
import "./customer-login-link/customer-login-link";
import "./dynamic-breadcrumb/dynamic-breadcrumb";
import "./refresh-recently-viewed/refresh-recently-viewed";
import "./product-card/product-card";

register();

// Load Swiper once for re-use by any other components
window.loadSwiperFulfillPromise({
  Swiper,
  Autoplay,
  EffectFade,
  EffectCreative,
  Navigation,
  Pagination,
  Mousewheel,
  Thumbs,
});

/**
 * Scrapes a section element for data needing to be handled in section JS
 *
 * @param {DOMNode} container
 * @returns parsed JSON
 */
window.scrapeSectionData = (container) => {
  const dataEl = container.querySelector("[data-section-data]");
  if (!dataEl) return {};

  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    console.warn(`Error parsing data for`, dataEl);
    return {};
  }
};

/**
 * Base-64 encodes an ID
 *
 * @param {String} id resource ID
 * @param {String} type resource type
 * @returns encoded ID
 */
window.encodeId = (id, type) => {
  return window.btoa(`gid://shopify/${type}/${id}`);
};

window.idToGid = (id, type) => {
  return `gid://shopify/${type}/${id}`;
};

window.gidToId = (id) => {
  return id.replace(/^\D*/, "");
};

window.productUrl = (handle) => {
  return `${window.routes.root_url === "/" ? "" : window.routes.root_url}/products/${handle}`;
};

/**
 * Decode Storefront Product ID, this is used
 * with Storefront API
 *
 * @param {String} id resource ID
 * @param {String} type resource type
 * @returns decoded ID
 */
window.decodeId = (id, type) => {
  return decodeURIComponent(escape(window.atob(id))).split(
    `gid://shopify/${type}/`,
  )[1];
};

/**
 * Formats a given integer to a standard fraction.
 *
 * @param {Integer} num A price, with cents
 * @param {Integer} fraction
 */
window.formatMoney = (num, showTrailingZeros, currency = "USD") => {
  const fractionDigits = showTrailingZeros
    ? 2
    : (num * 100) % 100 === 0
      ? 0
      : 2;

  return new Intl.NumberFormat("en-EN", {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

/**
 * Handleize string
 *
 * @param {string} `products array Object`
 * @returns handleized string object
 *
 */
window.handleize = (string) => {
  string = string.toLowerCase();

  const toReplace = ['"', "'", "\\", "(", ")", "[", "]"];

  // For the old browsers
  for (let i = 0; i < toReplace.length; ++i) {
    string = string.replace(toReplace[i], "");
  }

  string = string.replace(/\W+/g, "-");

  if (string.charAt(string.length - 1) == "-") {
    string = string.replace(/-+\z/, "");
  }

  if (string.charAt(0) == "-") {
    string = string.replace(/\A-+/, "");
  }

  return string;
};

// Resize Shopify Image URL
window.getSizedImageUrl = (src, size) => {
  // remove any current image size then add the new image size
  if (typeof src !== "undefined") {
    return src.replace(/\.jpg|\.png|\.webp|\.gif|\.jpeg/g, (match) => {
      return `_${size}${match}`;
    });
  } else {
    return "";
  }
};

/**
 * Fetchs page data from alternate templates
 *
 * @param {String} path
 * @param {String} templateSuffix
 * @returns parsed JSON
 */
window.fetchPageData = async ({ path, options = {} }) => {
  if (!path) return {};

  const { templateSuffix, sectionId, variantId } = options;
  const queryArray = [];

  if (templateSuffix) queryArray.push(`view=${templateSuffix}`);
  if (sectionId) queryArray.push(`section_id=${sectionId}`);
  if (variantId) queryArray.push(`variant=${variantId}`);

  const url = `${path}?${queryArray.join("&")}`;
  let html = {};

  await fetch(url)
    .then((response) => response.text())
    .then((response) => {
      const elId = sectionId ? `shopify-section-${sectionId}` : "MainContent";
      html = new DOMParser().parseFromString(response, "text/html");
      html = html.getElementById(elId).innerHTML;
    })
    .catch(() => {
      console.warn(`Error fetching data from ${url}`);
    });

  return html;
};

const lockers = new Set();

window.lockScroll = (locker) => {
  lockers.add(locker);
  document.documentElement.style.overflow = "hidden";
};

window.unlockScroll = (locker) => {
  lockers.delete(locker);

  if (!lockers.size) {
    document.documentElement.style.overflow = null;
  }
};

// Below header dimmer (for mega menus, search, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".below-header-dimmer");

  window.openDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();

// Full screen dimmer (for drawers, etc)
(() => {
  const openers = new Set();

  const dimmer = document.querySelector(".full-screen-dimmer");

  window.openFullDimmer = (opener) => {
    openers.add(opener);
    dimmer.classList.add("dimmer--active");
  };

  window.closeFullDimmer = (opener) => {
    openers.delete(opener);

    if (!openers.size) {
      dimmer.classList.remove("dimmer--active");
    }
  };
})();
